import React, { useMemo, useRef }  from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { Box, CircularProgress } from '@mui/material';
import { InitialIsAckRequiredDocumentsState, useDocumentActionsStatus } from 'PdfDocumentGrid/hooks/DocumentActionsStatus.context';
import { getStarredIsAckRequiredDocumentsCount } from 'PdfDocumentGrid/PdfDocumentsList/helpers';
import { styledPdfDocumentsList } from '../PdfDocumentGrid/PdfDocumentsList/stylesPdfDocumentsList';
import { ISynchronizationItemMeta, getAllSynchronizationItemsMeta } from 'utils/DexieDBManager';
import { useRedirectIfUnauthenticated } from 'utils/azureAD/useRedirectIfUnauthenticated';
import { getSynchronizationItemsAsync } from 'PdfDocumentGrid/pdfDocuments.service';
import { APPLICABLE_STATE_NAME } from 'utils/DexieDBManager/dexieDB.models';
import { filterFavoriteSynchronizationItemMetas } from './helper';
import PdfDocumentsList from 'PdfDocumentGrid/PdfDocumentsList';
import AlertMessage from 'Shared.Components/AlertMessage';
import { RESOURCES_KEYS } from 'utils/i18n/resources';
import { computeErrorMessage } from 'utils/utils';
import ErrorBoundary from 'utils/ErrorBoundary';

import './favoritesPage.scss';
import { isPwa } from 'utils/pwa';

const FavoritesPage: React.FunctionComponent = () => {
  const hasRun = useRef(false);
  const { t: translate } = useTranslation();
  const { accounts } = useRedirectIfUnauthenticated();
  const { state, dispatch } = useDocumentActionsStatus();
  const synchronizationItemsFromDb = useLiveQuery<ISynchronizationItemMeta[]>(() => getAllSynchronizationItemsMeta());
  const isOnline = navigator.onLine;
  const username = accounts[0]?.username;

  const { data: getSynchronizationItemsData, isLoading: isGetSynchronizationItemsLoading } = useQuery(
    ["starredSynchronizationItems", { isOnlyFav: true, username }],
    () => getSynchronizationItemsAsync(username, { isOnlyFav: true }),
    { 
      cacheTime: 0,
      staleTime: 0,
      enabled: isOnline && Boolean(username),
    }
  );

  const filteredSynchronizationItemsFromDb = filterFavoriteSynchronizationItemMetas(synchronizationItemsFromDb || []);
  const synchronizationItemsFromServer = useMemo(() => getSynchronizationItemsData?.responseData?.items || [], [getSynchronizationItemsData?.responseData?.items]);

  const errorPageTitle = translate(RESOURCES_KEYS.ERROR_BOUNDARY_TITLE, {page: translate(RESOURCES_KEYS.MENU_FAVORITES)});
  
  const isDataLoading = (isOnline && isGetSynchronizationItemsLoading && !synchronizationItemsFromServer.length) ||
                        (isPwa &&  !isOnline && !filteredSynchronizationItemsFromDb.length);

  React.useEffect(() => {
    if(isDataLoading) {
      return
    }

    if(hasRun.current) {
      return
    }

    if (isPwa && !isOnline) {
      filteredSynchronizationItemsFromDb.forEach((synchronization) => {

        const isCategoriesPageInitialized = state.categoriesIsAckRequiredDocumentsState.get(synchronization.id)?.isCategoriesPageInitialized;
        if (isCategoriesPageInitialized) {
          dispatch({
            type: "initCategoriesIsAckRequiredDocumentsState",
            isAckRequiredDocumentsState: {
              ...state.categoriesIsAckRequiredDocumentsState.get(synchronization.id),
              synchronizationId: synchronization.id,
              isCategoriesPageInitialized: false,
            } as InitialIsAckRequiredDocumentsState,
          });
        }
        
        const isStarredPageInitialized = state.starredIsAckRequiredDocumentsState.get(synchronization.id)?.isStarredPageInitialized;
        if (!isStarredPageInitialized) {
          dispatch({
            type: "initStarredIsAckRequiredDocumentsState",
            isAckRequiredDocumentsState: {
              isStarredPageInitialized: true,
              synchronizationId: synchronization.id,
              documentsVersionCount: synchronization.starredDocumentVersionsCount,
              applicablesDocumentsCount: synchronization.starredApplicableDocumentsCount,
              nextApplicablesDocumentsCount: synchronization.starredNextApplicableDocumentsCount,
              isAckRequiredApplicableDocumentsCount: synchronization.starredIsAckRequiredApplicableDocumentsCount,
              isAckRequiredNextApplicableDocumentsCount: synchronization.starredIsAckRequiredNextApplicableDocumentsCount,
            },
          });
        }

        const isAcknowledgementPageInitialized = state.acknowledgementIsAckRequiredDocumentsState.get(synchronization.id)?.isAcknowledgementPageInitialized;
        if (isAcknowledgementPageInitialized) {
          dispatch({
            type: "initAcknowledgementIsAckRequiredDocumentsState",
            isAckRequiredDocumentsState: {
              ...state.acknowledgementIsAckRequiredDocumentsState.get(synchronization.id),
              synchronizationId: synchronization.id,
              isAcknowledgementPageInitialized: false,
            } as InitialIsAckRequiredDocumentsState,
          });
        }
      });

      hasRun.current = true;

    } else if (isOnline && synchronizationItemsFromServer.length) {
      
      synchronizationItemsFromServer.forEach((synchronization) => {  

        const isCategoriesPageInitialized = state.categoriesIsAckRequiredDocumentsState.get(synchronization.id)?.isCategoriesPageInitialized;
        if (isCategoriesPageInitialized) {
          dispatch({
            type: "initCategoriesIsAckRequiredDocumentsState",
            isAckRequiredDocumentsState: {
              ...state.categoriesIsAckRequiredDocumentsState.get(synchronization.id),
              synchronizationId: synchronization.id,
              isCategoriesPageInitialized: false,
            } as InitialIsAckRequiredDocumentsState,
          });
        }
        
        const isStarredPageInitialized = state.starredIsAckRequiredDocumentsState.get(synchronization.id)?.isStarredPageInitialized;
        if (!isStarredPageInitialized) {
          const starredApplicablesCount = (synchronization.applicables)?.length ?? 0;
          const starredNextApplicablesCount = (synchronization.nextApplicables)?.length ?? 0;
          dispatch({
            type: "initStarredIsAckRequiredDocumentsState",
            isAckRequiredDocumentsState: {
              isStarredPageInitialized: true,
              synchronizationId: synchronization.id,
              applicablesDocumentsCount: starredApplicablesCount,
              nextApplicablesDocumentsCount: starredNextApplicablesCount,
              documentsVersionCount: starredApplicablesCount + starredNextApplicablesCount,
              isAckRequiredApplicableDocumentsCount: getStarredIsAckRequiredDocumentsCount(synchronization.applicables),
              isAckRequiredNextApplicableDocumentsCount: getStarredIsAckRequiredDocumentsCount(synchronization.nextApplicables),
            },
          });
        }

        const isAcknowledgementPageInitialized = state.acknowledgementIsAckRequiredDocumentsState.get(synchronization.id)?.isAcknowledgementPageInitialized;
        if (isAcknowledgementPageInitialized) {
          dispatch({
            type: "initAcknowledgementIsAckRequiredDocumentsState",
            isAckRequiredDocumentsState: {
              ...state.acknowledgementIsAckRequiredDocumentsState.get(synchronization.id),
              synchronizationId: synchronization.id,
              isAcknowledgementPageInitialized: false,
            } as InitialIsAckRequiredDocumentsState,
          });
        }
    
        synchronization.applicables.forEach((documentVersion) => {
          const isInitialized =state.documentActionsStatus.get(documentVersion.parentDocument.id)?.isInitialized;
          if (!isInitialized) {
            dispatch({
              type: "init",
              initialDocumentStatus: {
                isAckRequired: documentVersion.uds.isAck,
                isStarredDocument: documentVersion.uds.isFav,
                isUnread: !Boolean(documentVersion.uds.lastRead),
                parentDocumentId: documentVersion.parentDocument.id,
                documentVersionType: APPLICABLE_STATE_NAME.APPLICABLE,
              },
            });
          }
        });
    
        synchronization.nextApplicables.forEach((documentVersion) => {
          const isInitialized = Boolean(state.documentActionsStatus.get(documentVersion.parentDocument.id)?.isInitialized);
          if (!isInitialized) {
            dispatch({
              type: "init",
              initialDocumentStatus: {
                isAckRequired: documentVersion.uds.isAck,
                isStarredDocument: documentVersion.uds.isFav,
                isUnread: !Boolean(documentVersion.uds.lastRead),
                parentDocumentId: documentVersion.parentDocument.id,
                documentVersionType: APPLICABLE_STATE_NAME.NEXT_APPLICABLE,
              },
            });
          }
        });
      });
      hasRun.current = true;
    }
  
  }, [dispatch, isOnline, state, filteredSynchronizationItemsFromDb, synchronizationItemsFromServer, isGetSynchronizationItemsLoading, isDataLoading]);

  return (
    <ErrorBoundary title={errorPageTitle}>
      <Box id='starredPage' sx={styledPdfDocumentsList}>
        {isDataLoading ? (
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : getSynchronizationItemsData?.error ? (
          <AlertMessage title={<h2>{errorPageTitle}</h2>}>
            <h3>{`${translate(computeErrorMessage(getSynchronizationItemsData?.error))}, ${translate(RESOURCES_KEYS.PLEASE_CONTACT_SUPPORT_TEAM)}.`}</h3>
          </AlertMessage>
        ) : (
          <>
            {!synchronizationItemsFromDb?.length && !synchronizationItemsFromServer.length ? (
              <span className='favorite-page'>
                <AlertMessage severity='info' hasIcon={false}>
                  <h2>{translate(RESOURCES_KEYS.FAVORITES_EMPTY)}</h2>
                </AlertMessage>
              </span>
            ) : (
              <PdfDocumentsList
                synchronizationItemsFromDb={filteredSynchronizationItemsFromDb}
                synchronizationItemsFromServer={synchronizationItemsFromServer}      
              />
            )}
          </>
        )}
      </Box>
    </ErrorBoundary>
  );
};
export default FavoritesPage;
